import {
  type DatasourceType,
  type User as DSUser,
  type RichText
} from '@atlaskit/linking-types';
import { type Access } from '@atlaskit/linking-types';
import * as z from 'zod';

import {
  type ExceptCreate,
  type ExceptExisting,
  type ObjectMode,
  type OnlyExisting,
  type OnlyUpdate
} from '../types.ts';

export declare const DATABASE_CONTENT_TYPE = 'database';

export type ContentType =
  | 'page'
  | 'blogpost'
  | typeof DATABASE_CONTENT_TYPE
  | 'whiteboard'
  | 'embed';

type TypedContent<
  Type extends ContentType = ContentType,
  Mode extends ObjectMode = ObjectMode.EXISTING
> = {
  title: string;
  type: Type;
  body?: ContentBodies<Mode>;
  history?: ContentHistory;
  metadata?: ContentMetadata<Mode>;
  extensions?: ContentExtensions;
} & ExceptCreate<
  Mode,
  {
    id: string;
    referenceId?: string;
  }
> &
  ExceptExisting<
    Mode,
    {
      ancestors?: Array<{ id: string }>;
      container?: { id: string; type: string };
      space: Space<Mode>;
    }
  > &
  OnlyExisting<
    Mode,
    {
      ari?: string;
      ancestors?: Content<ContentType, Mode>[];
      container?: Content<ContentType, Mode>;
      space?: Space<Mode>;
      operations?: ContentOperation[];
      restrictions?: {
        [Operation in ContentRestrictionOperation]?: ContentRestrictions<ContentRestrictionOperation>;
      };
      version?: ContentVersion<Mode>;
      /**
       * The status of the content object. Inside Confluence, this is a string and can be any value, but common
       * values are current, archived, trashed, historical.
       */
      status: string;
      _links: Links;
    }
  > &
  OnlyUpdate<
    Mode,
    {
      version: ContentVersion<Mode>;
    }
  >;

/* This is used when fetching content from the get content by ID API. */
export type Content<
  Type extends ContentType = ContentType,
  Mode extends ObjectMode = ObjectMode.EXISTING
> = {
  [T in ContentType]: TypedContent<T, Mode>;
}[Type];

export type Space<Mode extends ObjectMode = ObjectMode.EXISTING> = {
  key: string;
} & OnlyExisting<
  Mode,
  {
    id: number;
    name: string;
    operations?: ContentOperation[];
    permissions?: ContentPermission[];
    homepage?: Content<'page'>;
  }
>;

export enum ContentStatus {
  CURRENT = 'current',
  DRAFT = 'draft',
  ARCHIVED = 'archived'
}

export interface ContentState {
  /** The ID of the content state. May be 0. */
  id: number;
  color: string;
  name: string;
}

export interface AvailableContentStates {
  /**
   * The "suggested statuses" configured in the space settings. If this is undefined, it means that the "suggest status"
   * feature is turned off.
   */
  spaceContentStates?: ContentState[];

  /**
   * Custom statuses that have been created by the current user on any pages in the Confluence instance.
   * If this is an empty array, it means that the user hasn't created any page statuses yet. If this is undefined,
   * it means that the custom statuses feature is disabled in the space settings, also meaning that no new statuses
   * can be created.
   */
  customContentStates?: ContentState[];
}

export type ContentBodies<Mode extends ObjectMode = ObjectMode.EXISTING> = {
  [Rep in ContentRepresentation]?: ContentBody<Rep, Mode>;
};

export enum ContentRepresentation {
  STORAGE = 'storage',
  EDITOR = 'editor',
  VIEW = 'view',
  EXPORT_VIEW = 'export_view',
  STYLED_VIEW = 'styled_view',
  ATLAS_DOC_FORMAT = 'atlas_doc_format'
}

export type ContentBody<
  Representation extends ContentRepresentation = ContentRepresentation,
  Mode extends ObjectMode = ObjectMode.EXISTING
> = {
  value: string;
  representation: Representation;
} & OnlyExisting<
  Mode,
  {
    webresource?: {
      uris?: {
        css?: string[];
      };
      superbatch?: {
        uris?: {
          css?: string[];
        };
      };
    };
  }
>;

export type ContentVersion<Mode extends ObjectMode = ObjectMode.EXISTING> = {
  number: number;
} & OnlyExisting<
  Mode,
  {
    message: string;
    when: string;
    friendlyWhen: string;
    by: User;
    content?: Content;
  }
> &
  ExceptExisting<
    Mode,
    {
      message?: string;
    }
  >;

export interface ContentHistory {
  lastUpdated?: ContentVersion;
  createdDate: string;
  createdBy: User;
}

export interface ContentOperation {
  operation: string;
  targetType: string;
}

export interface ContentPermission {
  subjects?: {
    user?: {
      results: KnownUser[];
    };
    group?: {
      results: Group[];
    };
  };
  anonymousAccess: boolean;
  operation: ContentOperation;
}

export enum ContentRestrictionOperation {
  ADMINISTER = 'administer',
  COPY = 'copy',
  CREATE = 'create',
  DELETE = 'delete',
  EXPORT = 'export',
  MOVE = 'move',
  PURGE = 'purge',
  PURGE_VERSION = 'purge_version',
  READ = 'read',
  RESTORE = 'restore',
  UPDATE = 'update',
  USE = 'use'
}

export interface ContentRestrictions<
  Operation extends ContentRestrictionOperation = ContentRestrictionOperation
> {
  operation: Operation;
  restrictions: {
    user?: { results: KnownUser[] };
    group?: { results: Group[] };
  };
}

export type ContentMetadata<Mode extends ObjectMode = ObjectMode.EXISTING> = {
  properties?: ContentProperties<Mode>;
  labels?: RestArray<ContentLabel, Mode>;
} & OnlyExisting<
  Mode,
  {
    currentuser?: {
      viewed?: {
        lastSeen: string;
        friendlyLastSeen: string;
      };
    };
  }
>;

export type ContentProperties<Mode extends ObjectMode = ObjectMode.EXISTING> = {
  [Key in keyof ContentPropertyValue]?: ContentProperty<Key, Mode>;
};

interface ContentPropertyValue {
  editor: 'v2';
  'content-appearance-published': 'full-width' | 'default';
  'cover-picture-id-published': string;
  'content-state-published': string;
}

export type ContentPropertyKey = keyof ContentPropertyValue;

export type ContentProperty<
  Key extends ContentPropertyKey,
  Mode extends ObjectMode = ObjectMode.EXISTING
> = {
  value: ContentPropertyValue[Key];
} & ExceptCreate<
  Mode,
  {
    key: Key;
    version: ContentVersion<Mode>;
  }
>;

export interface ContentLabel {
  prefix: 'global';
  name: string;
}

export interface ContentExtensions {
  fileId?: string;
}

export interface HighlightsValue {
  show: boolean;
  createdDate: string;
}

export interface AnonymousUser {
  type: 'anonymous';
  displayName: string;
  profilePicture: { path: string };
}

export interface KnownUser {
  type: 'known';
  accountId: string;
  displayName: string;
  profilePicture: { path: string };
}

export type User = AnonymousUser | KnownUser;

export interface Group {
  id: string;
  name: string;
}

export function isAnonymous(user: User): user is AnonymousUser {
  return user.type === 'anonymous';
}

export function isSameUser(a: User, b: User): boolean {
  return (
    (isAnonymous(a) && isAnonymous(b)) ||
    (!isAnonymous(a) && !isAnonymous(b) && a.accountId === b.accountId)
  );
}

export function isSameGroup(a: Group, b: Group): boolean {
  return a.id === b.id;
}

export function isUser(userOrGroup: User | Group): userOrGroup is User {
  return 'accountId' in userOrGroup;
}

export function isGroup(userOrGroup: User | Group): userOrGroup is Group {
  return 'id' in userOrGroup;
}

export function isSameUserOrGroup(a: User | Group, b: User | Group): boolean {
  return isGroup(a) && isGroup(b)
    ? isSameGroup(a, b)
    : !isGroup(a) && !isGroup(b)
      ? isSameUser(a, b)
      : false;
}

export type RestArray<T, Mode extends ObjectMode = ObjectMode.EXISTING> = {
  results: T[];
} & OnlyExisting<
  Mode,
  {
    _links: Links;
  }
>;

export type SearchResult<T extends Content | Space> = T extends Content
  ? {
      content: Content;
    }
  : {
      space: Space;
    };

export interface ErrorResult {
  status: number;
  message: string;
}

export interface Links {
  self?: string;
  next?: string;
  base?: string;
  webui?: string;
  download?: string;
}

export type Template = {
  templateId: string;
  name: string;
  editorVersion?: string;
  body?: ContentBodies;
  /** The space of the template (for space templates) or undefined for global templates. */
  space?: Space;
} & (
  | {
      templateType: 'page';
    }
  | {
      templateType: 'blueprint';
      referencingBlueprint: string;
      originalTemplate: { pluginKey: string; moduleKey: string };
    }
);

export type PageTemplate = Template & { templateType: 'blueprint' };

/** A zod schema to validate a page reference. */
export const pageReferenceValidator = z.object({
  type: z.literal('page'),
  spaceKey: z.string().min(1),
  contentTitle: z.string().min(1),
  contentId: z.string().optional(),
  ari: z.string().optional()
});

/* Raw content references from the model, can be used for page link values and
parent page field setting */
export type ContentReference = {
  contentId: string;
  ari?: string | undefined;
  _id?: string;
};

/* Used for page creation from dynamic parent, resolved content references with extra data */
export const resolvedContentReferenceValidator = z.object({
  type: z.enum(['page', 'blogpost', 'database', 'whiteboard', 'embed']),
  contentId: z.string().min(1),
  spaceKey: z.string().min(1),
  contentTitle: z.string().min(1),
  ari: z.string().optional(),
  url: z.string().optional()
});

export type ResolvedContentReference = z.infer<typeof resolvedContentReferenceValidator>;

/* Used in page picker when creating new pages */
export type CreatePageReference = {
  type: 'page';
  contentTitle: string;
  spaceKey: string;
  create: {
    /** If undefined, the page should be created on the root level of the space. */
    parentPageId: string | undefined;
  };
};

/** A reference by space key and page title to a Confluence page. */
export type PageReference<ExtraPageReferenceType = object> = z.infer<
  typeof pageReferenceValidator
> &
  ExtraPageReferenceType;

export type PageReferenceOrContentId =
  | PageReference<object>
  | {
      contentId: string;
    };

export type ResolvedReference = {
  contentId: string;

  /** The absolute URL to the view page. */
  url: string;
};

/** A page reference whose content ID and URL have been resolved. */
export type ResolvedPageReference = PageReference<{ contentId: string }> &
  ResolvedReference;
export type ResolvedPageReferenceOrContentId = PageReferenceOrContentId &
  ResolvedReference;

export type CreateOrResolvedPageReference =
  | RequiredCreatePageReference
  | (PageReference<{ contentId: string }> & ResolvedReference);

export type OptionalCreatePage = {
  /** If set, the referenced page does not exist yet and is supposed to be created under the specified parent page. */
  create?: {
    /** If undefined, the page should be created on the root level of the space. */
    parentPageId: string | undefined;
  };
};

/**
 * A reference by space key and page title to a Confluence page, with a flag indicating whether the referenced
 * page does not exist yet and is drafted for creation.
 */
export type OptionalCreatePageReference<ExtraPageReferenceType = object> =
  PageReference<ExtraPageReferenceType> & OptionalCreatePage;
export type OptionalCreatePageReferenceOrContentId<ExtraPageReferenceType = object> =
  | OptionalCreatePageReference<ExtraPageReferenceType>
  | { contentId: string };

export type RequiredCreatePageReference = PageReference & Required<OptionalCreatePage>;

/** A zod schema to validate a blogpost reference. */
export const blogpostReferenceValidator = z.object({
  type: z.literal('blogpost'),
  spaceKey: z.string().min(1),
  contentTitle: z.string().min(1),
  postingDay: z.string().min(1),
  contentId: z.string().optional(),
  ari: z.string().optional()
});

/** A reference by space key, content title and posting day to a Confluence blogpost. */
export type BlogpostReference = z.infer<typeof blogpostReferenceValidator>;

export type BlogpostReferenceOrContentId =
  | BlogpostReference
  | {
      contentId: string;
    };

/** A blogpost reference whose content ID and URL have been resolved. */
export type ResolvedBlogpostReference = BlogpostReference & ResolvedReference;
export type ResolvedBlogpostReferenceOrContentId = BlogpostReferenceOrContentId &
  ResolvedReference;

/** A zod schema to validate a space reference. */
export const spaceReferenceValidator = z.object({
  key: z.string().min(1)
});

/** A reference to a Confluence space by space key. */
export type SpaceReference = z.infer<typeof spaceReferenceValidator>;

/** A zod schema to validate a template reference. */
export const templateReferenceValidator = z.object({
  templateId: z.string().min(1),
  referencingBlueprint: z.string().optional(),
  name: z.string().optional(),
  editorVersion: z.string().optional()
});

/** A reference to a Confluence page template or blueprint. */
export type TemplateReference = z.infer<typeof templateReferenceValidator>;

/** A zod schema to validate a user reference. */
export const userReferenceValidator = z.object({
  accountId: z.string(),
  /** If this is provided, it can be used as the display name while the user is being loaded. It might not be up to date. */
  displayName: z.string().optional()
});

/** A reference to a Confluence user. */
export type UserReference = z.infer<typeof userReferenceValidator>;

export interface PermissionCheckResponse {
  hasPermission: boolean;
  errors: Array<{ translation: string; args: string[] }>;
}

/** A Jira server ID or undefined to use the primary application link. */
export type JiraServerId = string | undefined;

export type CloudId = string | undefined;

export interface JiraIssueRef {
  serverId?: JiraServerId;
  cloudId?: CloudId;
  key: string;
  id?: string;
  ari?: string;
  projectId?: string;
}

/**
 * Represents a Jira field that is passed to the "column" configuration of the Jira macro.
 * The column name can be the label of a custom or built-in Jira field (if it does not contain any special characters)
 * or the key of a custom or built-in Jira field, with a few exceptions:
 * - The issue key is "key", but not "issuekey"
 * - The issue type is "Issue type" or "type", but not "issuetype"
 * - The due date can be "due" in addition to "duedate" and "Due date"
 * The column name is case-insensitive.
 */
export type JiraColumn = string;

export type DatasourceUser = DSUser;
export const isDatasourceUser = (obj: any): obj is DatasourceUser => {
  return (
    obj && typeof obj.avatarSource === 'string' && typeof obj.displayName === 'string'
  );
};

export interface JiraIssueColumnData {
  html: string;
  text: string;
  type: string;
  data?: DatasourceType | RichText; // TODO: figure out why data is sometimes RichText instead of falling back as
}

export interface ResolvedJiraIssueRef extends JiraIssueRef {
  url: string;
  summary: string;
  statusCategory: 'to-do' | 'in-progress' | 'done';
  statusName: string;
  typeIconUrl: string;
  type: string;
  columns: Record<JiraColumn, JiraIssueColumnData>;
}

export type Authorization<T> =
  | { authorized: true; access: Access; data: T }
  | {
      authorized: false;
      access: Access;
    };

export interface JiraServer {
  id?: string;
  cloudId?: string;
  name: string;
}

export interface Excerpt {
  html?: string | null;
  text?: string | undefined | null;
}
